import { router, useForm } from "@inertiajs/react";
import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { type ScoreKey, type ScoreMap, SCORE_FIELDS, DEFAULT_SCORES } from "@/constants/forms";
import { ScoreSelector } from "@/components/votes";

interface Submission {
  id: number;
  photoUrl: string | null;
}

interface Flash {
  notice?: string;
  alert?: string;
}

interface User {
  email: string;
}

interface PageProps {
  submission: Submission | null;
  impulse: number;
  user: User;
  flash?: Flash;
  errors?: Record<string, string | string[]>;
}

interface FormState {
  submission_id: number | null;
  note: string;
  scores: ScoreMap;
}

export default function VotesIndex(props: PageProps) {
  const { submission, user, flash } = props;

  const {
    data,
    setData,
    post,
    processing,
    errors: formErrors,
  } = useForm<FormState>({
    submission_id: submission?.id ?? null,
    note: "",
    scores: { ...DEFAULT_SCORES },
  });

  const typedErrors = formErrors as Record<string, string | undefined>;
  const baseError = typedErrors.base;
  const noteError = typedErrors.note;

  useEffect(() => {
    if (submission?.id !== data.submission_id) {
      setData({
        submission_id: submission?.id ?? null,
        note: "",
        scores: { ...DEFAULT_SCORES },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id]);

  const handleScoreChange = (key: ScoreKey, value: number) => {
    setData((current) => ({
      ...current,
      scores: { ...current.scores, [key]: value },
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!submission) {
      router.reload({ only: ["submission", "impulse"] });
      return;
    }

    post("/votes", {
      preserveScroll: true,
    });
  };

  const QUICK_NOTES_POSITIVE = [
    "Ótima foto!",
    "Ótimo fundo!",
    "Ótimo sorriso!",
    "Ótimo visual!",
    "Profissional",
  ];

  const QUICK_NOTES_NEGATIVE = [
    "Agressivo(a)",
    "Arrogante",
    "Sem graça",
    "Falso(a)",
    "Sorriso forçado",
    "Intenso(a)",
    "Desatualizado(a)",
    "Triste",
    "Tímido(a)",
    "Cansado(a)",
    "Muito sedutor(a)",
    "Desconfortável",
    "Não profissional",
    "Jovem demais",
  ];

  const toggleQuickNote = (note: string) => {
    setData((current) => {
      const existing = current.note.trim();
      const notes = existing ? existing.split(", ") : [];
      const idx = notes.indexOf(note);
      if (idx >= 0) {
        notes.splice(idx, 1);
      } else {
        notes.push(note);
      }
      return { ...current, note: notes.join(", ") };
    });
  };

  const activeNotes = data.note.trim() ? data.note.trim().split(", ") : [];

  return (
    <Layout title="Votar" user={user}>
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
        <div className="space-y-3">
          {flash?.notice && (
            <Alert variant="success">
              <AlertDescription>{flash.notice}</AlertDescription>
            </Alert>
          )}
          {flash?.alert && (
            <Alert variant="warning">
              <AlertDescription>{flash.alert}</AlertDescription>
            </Alert>
          )}
          {baseError && (
            <Alert variant="error">
              <AlertDescription>{baseError}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.15fr_1fr]">
          {submission ? (
            <>
              <div className="overflow-hidden rounded-lg border border-border bg-background shadow-inner">
                {submission.photoUrl ? (
                  <img
                    src={submission.photoUrl}
                    alt="Foto"
                    className="w-full"
                  />
                ) : (
                  <div className="flex aspect-square items-center justify-center bg-card text-muted-foreground">
                    Sem imagem
                  </div>
                )}
              </div>

              <Card className="border border-border bg-card backdrop-blur">
                <CardContent className="space-y-5 pt-6">
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-3 gap-3">
                      {SCORE_FIELDS.map((field) => (
                        <ScoreSelector
                          key={field.key}
                          label={field.label}
                          hint={field.hint}
                          color={field.color}
                          value={data.scores[field.key]}
                          disabled={processing}
                          onChange={(value) => handleScoreChange(field.key, value)}
                        />
                      ))}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="note" className="text-sm text-muted-foreground">
                        Nota opcional
                      </Label>
                      <textarea
                        id="note"
                        value={data.note}
                        disabled={processing}
                        onChange={(event) =>
                          setData((form) => ({ ...form, note: event.target.value }))
                        }
                        className="min-h-[96px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:shadow-[0_0_0_3px] focus:shadow-primary/20 disabled:opacity-60"
                        placeholder="Compartilhe um comentário ou sugestão (opcional)"
                      />
                      {noteError && <p className="text-xs text-red-300">{noteError}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Notas rápidas</Label>
                      <div className="flex flex-wrap gap-2">
                        {QUICK_NOTES_POSITIVE.map((note) => (
                          <button
                            key={note}
                            type="button"
                            disabled={processing}
                            onClick={() => toggleQuickNote(note)}
                            className={`rounded-full border px-3 py-1 text-xs transition select-none ${
                              activeNotes.includes(note)
                                ? "border-emerald-500 bg-emerald-500/20 text-emerald-300"
                                : "border-border bg-muted text-muted-foreground hover:border-border/80 hover:text-foreground"
                            } disabled:opacity-60`}
                          >
                            👍 {note}
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {QUICK_NOTES_NEGATIVE.map((note) => (
                          <button
                            key={note}
                            type="button"
                            disabled={processing}
                            onClick={() => toggleQuickNote(note)}
                            className={`rounded-full border px-3 py-1 text-xs transition select-none ${
                              activeNotes.includes(note)
                                ? "border-red-500 bg-red-500/20 text-red-300"
                                : "border-border bg-muted text-muted-foreground hover:border-border/80 hover:text-foreground"
                            } disabled:opacity-60`}
                          >
                            {note}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        size="lg"
                        className="gap-2 px-6"
                        disabled={processing || !submission}
                      >
                        {processing ? "Enviando..." : "Enviar voto"}
                        <ArrowRight size={16} />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border border-border bg-card backdrop-blur lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-2xl">Nenhuma foto para avaliar no momento</CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Novas fotos aparecerão assim que estiverem disponíveis.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-end">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-2 text-muted-foreground hover:text-foreground"
                    onClick={() => router.visit("/submissions/new")}
                  >
                    Enviar foto
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    className="gap-2"
                    onClick={() => router.reload({ only: ["submission", "impulse"] })}
                  >
                    Atualizar
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
